import { useCallback, useMemo, useRef, useState } from 'react';
import { CouchVariant } from '~/domain/couchtop/couchLibrary';
import { DicomColor } from '~/domain/dicom/dicomColor';
import { validateStructureName } from '~/utils/validation';

/**
 * Default swatch colour for a not-yet-customised couch structure: neutral
 * grey (MUI `grey[500]`), passed EXPLICITLY. `useGetOrCreateDestination`
 * applies the house "operation green" when a colour is omitted, which
 * would read as a derived-structure preview rather than hardware — a
 * couch component is neither.
 */
const DEFAULT_STRUCTURE_COLOR: DicomColor = { R: 158, G: 158, B: 158 };

/** One editable destination structure, index-aligned with the variant's
 *  components. */
export interface StructureRowEdit {
    name: string;
    color: DicomColor;
}

export interface CouchStructureRows {
    /** Index-aligned with the selected variant's components; empty while
     *  no couch is selected. */
    readonly rows: readonly StructureRowEdit[];
    /** Per-row validation message, or null. Index-aligned with `rows`. */
    readonly nameErrors: readonly (string | null)[];
    readonly allNamesValid: boolean;
    /** One colour per row, with an identity stable across renders that
     *  changed no colour — see {@link useCouchStructureRows}. */
    readonly colors: readonly DicomColor[];
    readonly updateRow: (
        index: number,
        patch: Partial<StructureRowEdit>
    ) => void;
}

/**
 * Fresh rows for `variant`, named directly from each component's own
 * `roiName` — no uniquifier. The panel is subject to the one-couch rule
 * (any SUPPORT ROI blocks Apply), so a same-set collision on a first seed
 * would already mean Apply is disabled for that reason; silently handing
 * the user a suffixed `CouchShell_1` instead of surfacing the collision
 * would hide a name a TPS operator would otherwise expect to see verbatim.
 * The live per-row check still catches a genuine collision — a non-SUPPORT
 * structure that happens to hold the name — and the row shows the
 * duplicate error for the user to rename.
 */
function defaultRowEdits(variant: CouchVariant): StructureRowEdit[] {
    return variant.components.map((component) => ({
        name: component.roiName,
        color: DEFAULT_STRUCTURE_COLOR,
    }));
}

/**
 * The editable destination structures for a couch variant: one row per
 * component, re-seeded whenever the variant changes, validated against the
 * structure set and against each other.
 *
 * Rows are stored WITH the variant they were seeded for, so a rows/variant
 * mismatch is unrepresentable in committed state. The re-seed happens
 * DURING render (React's adjust-state-during-render pattern) rather than in
 * an effect: an effect would commit one frame pairing the old variant's
 * rows with the new variant's components — old names under new labels, and
 * a ghost preview republished mid-mismatch. React discards this render and
 * re-renders with the fresh rows, so the mismatched pairing never reaches
 * the DOM. That discarded render still EXECUTES, so the fresh rows are also
 * returned for THIS pass: indexing the old, shorter rows by the new
 * variant's components overflows when the new variant has more of them
 * (Radixact's five against everyone else's two).
 *
 * `variant` must be a stable reference for a given selection — the bundled
 * library const satisfies this — so the re-seed fires only on a genuine
 * change.
 */
export function useCouchStructureRows(
    variant: CouchVariant | null,
    existingNames: readonly string[]
): CouchStructureRows {
    const [state, setState] = useState<{
        variant: CouchVariant | null;
        rows: StructureRowEdit[];
    }>({ variant: null, rows: [] });

    let rows = state.rows;
    if (state.variant !== variant) {
        rows = variant ? defaultRowEdits(variant) : [];
        setState({ variant, rows });
    }

    const updateRow = useCallback(
        (index: number, patch: Partial<StructureRowEdit>) => {
            setState((prev) => ({
                variant: prev.variant,
                rows: prev.rows.map((row, i) =>
                    i === index ? { ...row, ...patch } : row
                ),
            }));
        },
        []
    );

    // Each row is validated against the structure set PLUS the OTHER rows:
    // two rows sharing a name would otherwise both pass, and the second
    // Apply would write a duplicate SUPPORT ROI with density overrides at a
    // different depth. Uses the house validator (empty / non-ASCII /
    // over-64-char / backslash / duplicate) rather than a local emptiness
    // check, so a couch name is held to the same rule as any other.
    const nameErrors = useMemo(
        () =>
            rows.map(
                (row, index) =>
                    validateStructureName(row.name, [
                        ...existingNames,
                        ...rows
                            .filter((_, i) => i !== index)
                            .map((other) => other.name),
                    ])[1]
            ),
        [rows, existingNames]
    );

    // The ghost consumes ONLY the components' colours (never the editable
    // names), and it re-places the couch on every republish — so this array
    // must keep its identity across renders that changed nothing it draws.
    // A name keystroke replaces the row object but not the untouched rows'
    // colour OBJECTS, so an element-wise identity check keeps the previous
    // array unless a colour actually changed. Written during render on
    // purpose: the stable value must be ready for the same render's preview
    // effect, and the write is idempotent for a given `rows`.
    const derived = rows.map((row) => row.color);
    const colorsRef = useRef<readonly DicomColor[]>(derived);
    if (
        derived.length !== colorsRef.current.length ||
        derived.some((c, i) => c !== colorsRef.current[i])
    ) {
        colorsRef.current = derived;
    }

    return {
        rows,
        nameErrors,
        allNamesValid: nameErrors.every((e) => e === null),
        colors: colorsRef.current,
        updateRow,
    };
}
